import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import td from "@/lib/i18n-dynamic";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  CreditCard, Landmark, ArrowRight, ArrowLeft, CheckCircle2, Loader2, Shield,
  FileText, Copy, ExternalLink, X
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLANS = [
  { id: "STARTER", name: "Starter", price: 29, desc: "See the Truth" },
  { id: "GROWTH", name: "Growth", price: 99, desc: "Take Control", popular: true },
  { id: "PRO", name: "Pro", price: 249, desc: "Stay Protected" },
];

const TERMS = [
  { months: 1, label: "Monthly Rolling", discount: null, desc: "Flexible monthly billing. Cancel anytime.", tag: "FLEXIBLE" },
  { months: 6, label: "6-Month Commitment", discount: "15% off", desc: "Pay upfront and save 15%.", tag: "SAVINGS" },
  { months: 12, label: "12-Month Commitment", discount: "15% off", desc: "Pay upfront and save 15%.", tag: "BEST VALUE" },
];

const CURRENCIES = [
  { code: "GBP", symbol: "£", label: "British Pound" },
  { code: "USD", symbol: "$", label: "US Dollar" },
  { code: "EUR", symbol: "€", label: "Euro" },
  { code: "AED", symbol: "AED", label: "UAE Dirham" },
];

export default function CheckoutPage() {
  const { token, user, formatMoney, language } = useAuth();
  const lang = language || "en";
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [pricing, setPricing] = useState(null);

  const [form, setForm] = useState({
    plan: searchParams.get("plan") || "GROWTH",
    term_months: 1,
    currency: "GBP",
    payment_method: "card",
    company_name: "", company_country: "GB", company_address: "",
    company_email: user?.email || "", company_vat: "",
    contact_name: user?.full_name || "", contact_email: user?.email || "",
  });

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const selectedPlan = PLANS.find(p => p.id === form.plan);
  const selectedCurrency = CURRENCIES.find(c => c.code === form.currency);

  // Calculate pricing when plan/term/currency changes
  useEffect(() => {
    if (form.plan && form.term_months && form.currency) {
      axios.get(`${API}/payment/pricing?plan=${form.plan}&term=${form.term_months}&currency=${form.currency}&country=${form.company_country}`)
        .then(r => setPricing(r.data)).catch(() => {});
    }
  }, [form.plan, form.term_months, form.currency, form.company_country]);

  const handleCheckout = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/payment/checkout`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResult(res.data);
      setStep(5);
    } catch (e) {
      setResult({ error: e.response?.data?.detail || "Checkout failed" });
    }
    setLoading(false);
  };

  const fmtLocal = (amount) => {
    const sym = selectedCurrency?.symbol || "£";
    return `${sym} ${amount?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "0.00"}`;
  };

  return (
    <div className="min-h-screen bg-[#020617] text-slate-50 py-8 px-4" data-testid="checkout-page">
      <div className="max-w-2xl mx-auto">
        {/* Back to Dashboard */}
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => navigate("/dashboard")} className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-300 transition-colors" data-testid="checkout-back-dashboard">
            <ArrowLeft className="w-3 h-3" /> {td(lang, "checkout.returnDashboard")}
          </button>
        </div>

        {/* Header */}
        <div className="text-center mb-6">
          <img src="https://customer-assets.emergentagent.com/job_vault-truth/artifacts/s3q6fukn_image.png" alt="" className="w-12 h-12 mx-auto mb-3" />
          <h1 className="font-barlow font-bold text-2xl">{td(lang, "checkout.upgradeTitle")}</h1>
          <p className="text-sm text-slate-500 mt-1">{td(lang, "checkout.upgradeSubtitle")}</p>
        </div>

        {/* Step indicators */}
        <div className="flex justify-center gap-2 mb-2">
          {[td(lang,"checkout.choosePlan"), td(lang,"checkout.chooseTerm"), td(lang,"checkout.enterDetails"), td(lang,"checkout.choosePayment"), td(lang,"checkout.confirmPay")].map((s, i) => (
            <div key={i} className={`flex items-center gap-1 ${i + 1 <= step ? 'text-amber-400' : 'text-slate-700'}`}>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-mono ${i + 1 <= step ? 'bg-amber-500/20 border border-amber-500/40' : 'bg-slate-800 border border-slate-700'}`}>
                {i + 1 <= step ? <CheckCircle2 className="w-3 h-3" /> : i + 1}
              </div>
              <span className="text-[9px] hidden md:inline">{s}</span>
              {i < 4 && <div className={`w-6 h-px ${i + 1 < step ? 'bg-amber-500/40' : 'bg-slate-800'}`} />}
            </div>
          ))}
        </div>
        <p className="text-center text-[10px] text-slate-600 mb-6">{td(lang, "checkout.safeExit")}</p>

        {/* Step 1: Plan */}
        {step === 1 && (
          <div className="space-y-3" data-testid="checkout-step-plan">
            {PLANS.map(p => (
              <button key={p.id} onClick={() => { set("plan", p.id); setStep(2); }}
                className={`w-full p-4 rounded-xl border text-left transition-all ${form.plan === p.id ? 'border-amber-500/50 bg-amber-500/5' : 'border-slate-800/50 bg-slate-900/30 hover:border-slate-700'}`}
                data-testid={`checkout-plan-${p.id.toLowerCase()}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-barlow font-bold">{p.name}</span>
                      {p.popular && <Badge className="bg-cyan-500/15 text-cyan-400 text-[8px] border-none">POPULAR</Badge>}
                    </div>
                    <span className="text-xs text-slate-500">{p.desc}</span>
                  </div>
                  <div className="text-right">
                    <span className="text-2xl font-mono font-bold text-slate-100">£{p.price}</span>
                    <span className="text-xs text-slate-500">/mo</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Step 2: Term */}
        {step === 2 && (
          <div className="space-y-3" data-testid="checkout-step-term">
            <h2 className="font-barlow font-semibold text-lg mb-4">{selectedPlan?.name} Plan — Choose Billing Term</h2>
            {TERMS.map(t => (
              <button key={t.months} onClick={() => { set("term_months", t.months); setStep(3); }}
                className={`w-full p-4 rounded-xl border text-left transition-all ${form.term_months === t.months ? 'border-amber-500/50 bg-amber-500/5' : 'border-slate-800/50 bg-slate-900/30 hover:border-slate-700'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold">{t.label}</span>
                      {t.discount && <Badge className="bg-emerald-500/15 text-emerald-400 text-[8px] border-none">{t.discount}</Badge>}
                      {t.tag === "BEST VALUE" && <Badge className="bg-amber-500/15 text-amber-400 text-[8px] border-none">{t.tag}</Badge>}
                      {t.tag === "FLEXIBLE" && <Badge className="bg-cyan-500/15 text-cyan-400 text-[8px] border-none">{t.tag}</Badge>}
                    </div>
                    <span className="text-xs text-slate-500 mt-0.5 block">{t.desc}</span>
                  </div>
                  <span className="font-mono text-slate-300">
                    {t.months === 1 ? `£${selectedPlan?.price}/mo` : `£${Math.round(selectedPlan?.price * t.months * (t.discount ? 0.85 : 1)).toLocaleString()}`}
                  </span>
                </div>
              </button>
            ))}
            <Button variant="outline" className="border-slate-700 mt-2" onClick={() => setStep(1)}>Back</Button>
          </div>
        )}

        {/* Step 3: Company Details */}
        {step === 3 && (
          <div className="space-y-4" data-testid="checkout-step-details">
            <h2 className="font-barlow font-semibold text-lg mb-2">Company Details</h2>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label className="text-[10px] text-slate-500">Company Name</Label><Input value={form.company_name} onChange={e => set("company_name", e.target.value)} className="bg-slate-900/50 border-slate-700 mt-1" data-testid="checkout-company-name" /></div>
              <div><Label className="text-[10px] text-slate-500">Country</Label>
                <Select value={form.company_country} onValueChange={v => set("company_country", v)}>
                  <SelectTrigger className="bg-slate-900/50 border-slate-700 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700"><SelectItem value="GB">United Kingdom</SelectItem><SelectItem value="US">United States</SelectItem><SelectItem value="AE">UAE</SelectItem><SelectItem value="DE">Germany</SelectItem><SelectItem value="FR">France</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label className="text-[10px] text-slate-500">Currency</Label>
                <Select value={form.currency} onValueChange={v => set("currency", v)}>
                  <SelectTrigger className="bg-slate-900/50 border-slate-700 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">{CURRENCIES.map(c => <SelectItem key={c.code} value={c.code}>{c.code} ({c.symbol})</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label className="text-[10px] text-slate-500">Email</Label><Input value={form.company_email} onChange={e => set("company_email", e.target.value)} className="bg-slate-900/50 border-slate-700 mt-1" /></div>
              <div><Label className="text-[10px] text-slate-500">Contact Name</Label><Input value={form.contact_name} onChange={e => set("contact_name", e.target.value)} className="bg-slate-900/50 border-slate-700 mt-1" /></div>
              <div><Label className="text-[10px] text-slate-500">VAT Number (optional)</Label><Input value={form.company_vat} onChange={e => set("company_vat", e.target.value)} className="bg-slate-900/50 border-slate-700 mt-1" /></div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button variant="outline" className="border-slate-700" onClick={() => setStep(2)}>Back</Button>
              <Button className="flex-1 bg-amber-600 hover:bg-amber-500 text-slate-900" onClick={() => setStep(4)} disabled={!form.company_name || !form.company_email} data-testid="checkout-to-payment">
                Continue to Payment <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Payment Method + Summary */}
        {step === 4 && (
          <div className="space-y-4" data-testid="checkout-step-payment">
            {/* Pricing Summary */}
            {pricing && (
              <Card className="bg-slate-900/40 border-slate-800/50">
                <CardContent className="p-4 space-y-2">
                  <div className="flex justify-between text-sm"><span className="text-slate-400">{pricing.plan_name} x {pricing.term_months} months</span><span className="font-mono">{fmtLocal(pricing.subtotal_gbp * pricing.fx_rate + pricing.discount_gbp * pricing.fx_rate)}</span></div>
                  {pricing.discount_pct > 0 && <div className="flex justify-between text-sm"><span className="text-emerald-400">Discount ({pricing.discount_pct * 100}%)</span><span className="font-mono text-emerald-400">-{fmtLocal(pricing.discount_gbp * pricing.fx_rate)}</span></div>}
                  {pricing.tax_rate > 0 && <div className="flex justify-between text-sm"><span className="text-slate-400">Tax ({(pricing.tax_rate * 100).toFixed(0)}%)</span><span className="font-mono">{fmtLocal(pricing.tax_gbp * pricing.fx_rate)}</span></div>}
                  {pricing.currency !== "GBP" && <div className="text-[9px] text-slate-600">FX Rate: 1 GBP = {pricing.fx_rate} {pricing.currency}</div>}
                  <div className="border-t border-slate-800 pt-2 flex justify-between"><span className="text-slate-200 font-semibold">Total</span><span className="text-xl font-mono font-bold text-amber-400">{fmtLocal(pricing.total_local)}</span></div>
                </CardContent>
              </Card>
            )}

            {/* Payment Method */}
            <h2 className="font-barlow font-semibold text-lg">{td(lang, "checkout.choosePayment")}</h2>
            <div className="space-y-2">
              {[
                { id: "card", icon: CreditCard, label: "Card Payment", desc: td(lang, "checkout.cardDesc"), color: "text-cyan-400" },
                { id: "paypal", icon: ExternalLink, label: "PayPal", desc: td(lang, "checkout.paypalDesc"), color: "text-blue-400" },
                { id: "bank_transfer", icon: Landmark, label: "Bank Transfer", desc: td(lang, "checkout.bankDesc"), color: "text-amber-400" },
              ].map(m => (
                <button key={m.id} onClick={() => set("payment_method", m.id)}
                  className={`w-full p-3 rounded-xl border text-left flex items-center gap-3 transition-all ${form.payment_method === m.id ? 'border-amber-500/50 bg-amber-500/5' : 'border-slate-800/50 bg-slate-900/30 hover:border-slate-700'}`}
                  data-testid={`payment-method-${m.id}`}>
                  <m.icon className={`w-5 h-5 ${m.color}`} />
                  <div><span className="text-sm text-slate-200">{m.label}</span><span className="text-[10px] text-slate-500 block">{m.desc}</span></div>
                  {form.payment_method === m.id && <CheckCircle2 className="w-4 h-4 text-amber-400 ml-auto" />}
                </button>
              ))}
            </div>

            <div className="p-3 rounded-lg border border-slate-800/20 bg-slate-900/20">
              <div className="flex items-center gap-1.5"><Shield className="w-3 h-3 text-emerald-400" /><span className="text-[9px] text-slate-600">{td(lang, "checkout.nonCustodial")}</span></div>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" className="border-slate-700" onClick={() => setStep(3)}>Back</Button>
              <Button className="flex-1 bg-gradient-to-r from-amber-500 to-amber-600 text-slate-900 font-semibold" onClick={handleCheckout} disabled={loading} data-testid="checkout-pay-btn">
                {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Processing...</> : `Pay ${pricing ? fmtLocal(pricing.total_local) : ""}`}
              </Button>
            </div>
          </div>
        )}

        {/* Step 5: Confirmation */}
        {step === 5 && result && (
          <div className="space-y-4" data-testid="checkout-step-confirm">
            {result.error ? (
              <Card className="border-red-500/20 bg-red-500/5"><CardContent className="p-4 text-red-400">{result.error}</CardContent></Card>
            ) : (
              <>
                <div className="text-center mb-6">
                  <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
                  <h2 className="font-barlow font-bold text-2xl text-slate-50">{td(lang, "checkout.orderCreated")}</h2>
                  <p className="text-sm text-slate-500 mt-1">Invoice: {result.invoice?.invoice_number}</p>
                </div>

                {/* Stripe redirect */}
                {result.stripe?.url && (
                  <a href={result.stripe.url} className="block w-full p-4 rounded-xl border border-cyan-500/30 bg-cyan-500/5 text-center hover:bg-cyan-500/10 transition-colors" data-testid="stripe-pay-link">
                    <CreditCard className="w-5 h-5 text-cyan-400 mx-auto mb-1" />
                    <span className="text-sm text-cyan-400 font-semibold">Complete Card Payment</span>
                    <span className="text-[10px] text-slate-500 block">Secure checkout via Stripe</span>
                  </a>
                )}

                {/* PayPal redirect */}
                {result.paypal_url && (
                  <a href={result.paypal_url} target="_blank" rel="noopener" className="block w-full p-4 rounded-xl border border-blue-500/30 bg-blue-500/5 text-center hover:bg-blue-500/10 transition-colors" data-testid="paypal-pay-link">
                    <ExternalLink className="w-5 h-5 text-blue-400 mx-auto mb-1" />
                    <span className="text-sm text-blue-400 font-semibold">Pay with PayPal</span>
                    <span className="text-[10px] text-slate-500 block">Amount pre-filled: {pricing ? fmtLocal(pricing.total_local) : ""}</span>
                  </a>
                )}

                {/* Bank Transfer */}
                {result.bank_details && (
                  <Card className="border-amber-500/20 bg-amber-500/5">
                    <CardHeader className="pb-2"><CardTitle className="text-sm text-amber-400">Bank Transfer Details</CardTitle></CardHeader>
                    <CardContent className="space-y-2">
                      {Object.entries(result.bank_details).map(([k, v]) => (
                        <div key={k} className="flex justify-between text-xs">
                          <span className="text-slate-500 capitalize">{k.replace(/_/g, " ")}</span>
                          <span className="text-slate-200 font-mono">{v}</span>
                        </div>
                      ))}
                      <div className="pt-2 border-t border-amber-500/10">
                        <div className="flex justify-between text-xs"><span className="text-slate-500">Reference</span><span className="text-amber-400 font-mono">{result.invoice?.invoice_number}</span></div>
                        <div className="flex justify-between text-xs"><span className="text-slate-500">Amount</span><span className="text-amber-400 font-mono font-bold">{pricing ? fmtLocal(pricing.total_local) : ""}</span></div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                <div className="p-3 rounded-lg border border-slate-800/20 bg-slate-900/20 text-center">
                  <p className="text-[9px] text-slate-600">Sentient Vault™ is a venture of Celestial Minds FZE. All payments are received by Celestial Minds FZE.</p>
                </div>

                <Button className="w-full bg-slate-800 hover:bg-slate-700" onClick={() => navigate("/dashboard")}>
                  Return to Dashboard
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
